/** Para `t.jose.nombre`: las preguntas y los casos de José, como los dice el operador. */
const PREGUNTAS_JOSE: Readonly<Record<string, string>> = {
  budget: "Presupuesto",
  authority: "Quién decide",
  timing: "Plazo",
  competition: "Competencia",
  trust: "Confianza",
  etapa: "Etapa",
};
const CASOS_JOSE: Readonly<Record<string, string>> = { noLoSe: "no lo sabe", desfavorable: "en contra" };

/**
 * Todo el texto visible del backoffice, en un sitio (misma disciplina que
 * `OD-0017 D-07` en el producto). Una sola lengua: el público son las personas
 * que operan Faktelio.
 */
export const t = {
  cinta: {
    titulo: "Faktelio · interno",
    aviso: "datos de clientes reales · cada ficha queda registrada",
    empresas: "Empresas",
    apoderamientos: "Apoderamientos",
    fundadores: "Fundadores",
    jose: "José",
    correos: "Correos",
    salir: "Cerrar sesión",
  },
  acceso: {
    titulo: "Acceso interno",
    email: "Correo",
    password: "Contraseña",
    entrar: "Entrar",
    codigo: "Código de la aplicación de autenticación",
    verificar: "Verificar",
    credenciales: "El correo o la contraseña no son correctos.",
    codigoInvalido: "El código no es válido o ha caducado.",
    bloqueado: "Demasiados intentos. Prueba más tarde.",
    error: "No se ha podido completar el acceso.",
  },
  comun: {
    cargando: "Cargando…",
    noEncontrado: "No se ha encontrado.",
    sinMfa: "Esta herramienta exige segundo factor. Actívalo en tu cuenta y vuelve a entrar.",
    error: "Algo ha fallado. Inténtalo de nuevo.",
    volver: "Volver",
    sinDatos: "Sin datos.",
    si: "Sí",
    no: "No",
  },
  empresas: {
    titulo: "Empresas",
    resumen: (n: number): string => `${n} empresa(s). El listado no registra acceso a los datos de nadie.`,
    columnas: {
      nombre: "Nombre",
      id: "Identificador",
      plan: "Plan",
      estado: "Estado",
      alta: "Alta",
      shard: "Shard",
    },
    /*
      Nada baja un plan mientras GATE-04 siga en PENDING, asi que esto no
      anuncia una accion automatica: avisa de que hay que decidir a mano. El
      texto dice el HECHO -paga un plan y la suscripcion no esta viva- y no
      "impagado", que seria una conclusion que ni el codigo ni el dato
      sostienen: una suscripcion CANCELED puede estar pagada hasta fin de
      periodo.
    */
    sinSuscripcionViva: "plan de pago sin suscripcion viva",
    suscripcionSinSaber: "suscripcion: no se pudo leer",
    sinSuscripcionVivaDetalle: (estado: string | null): string =>
      estado === null
        ? "Tiene un plan de pago y no consta ninguna suscripcion. Nada lo baja solo (GATE-04)."
        : `Tiene un plan de pago y su suscripcion esta ${estado}. Nada lo baja solo (GATE-04).`,
    /*
      OD-0064 D-12. Una organizacion en su prueba paga Business sin
      suscripcion y es lo previsto: esta linea dice POR QUE no se la senala. Si
      la prueba ya debio terminar y sigue en Business, la alarma de arriba sale
      igual, y esta linea explica de donde viene.
    */
    prueba: (estado: "EN_CURSO" | "CONVERTIDA" | "TERMINADA", ultimoDia: string): string =>
      estado === "EN_CURSO"
        ? `prueba de Business hasta el ${ultimoDia}`
        : estado === "CONVERTIDA"
          ? "pago durante su prueba de Business"
          : `prueba de Business terminada el ${ultimoDia}, sin pago`,
    /* OD-0064 D-12: contadores de los ultimos 30 dias. Las convertidas, por version de plan, tal cual. */
    pruebas: (r: {
      readonly empezadas: number;
      readonly enCurso: number;
      readonly terminadasSinPagar: number;
      readonly convertidas: Readonly<Record<string, number>>;
    }): string => {
      const convertidas = Object.entries(r.convertidas);
      const detalle = convertidas.length === 0 ? "ninguna" : convertidas.map(([v, n]) => `${n} a ${v}`).join(", ");
      return `Prueba de Business, ultimos 30 dias: ${r.empezadas} empezada(s), ${r.terminadasSinPagar} terminada(s) sin pagar, convertidas: ${detalle}. Ahora en curso: ${r.enCurso}.`;
    },
    desconocida: "sin ficha en el censo",
    filtros: { TODAS: "Todas", ACTIVE: "Activas", CLOSED: "Cerradas", UNKNOWN: "Incoherentes" } as Readonly<Record<string, string>>,
    sinResultados: "Ninguna con ese estado.",
    verFicha: "Ver ficha",
  },
  empresa: {
    titulo: "Ficha de la empresa",
    registrado: "Abrir esta ficha ha quedado registrado en los datos del cliente: él puede verlo.",
    sinFacturas: "Esta ficha no muestra facturas, ni importes, ni datos de clientes finales.",
    emisor: "Obligado a expedir",
    sinEmisor: "Todavía no ha dado de alta su obligado a expedir.",
    nombre: "Razón social",
    nif: "NIF",
    confirmado: "Confirmado",
    representacion: "Autorización para remitir a la AEAT",
    remision: "Remisión a la AEAT",
    sinRemision: "Nada en cola.",
    pendientes: "Pendientes de envío",
    rechazados: "Rechazados por la AEAT",
    incidencia: "Incidencia abierta (art. 16.4)",
    envios: "Últimos envíos",
    enviosVacio: "Todavía no ha salido ningún envío.",
    enviosColumnas: { cuando: "Cuándo", estado: "Respuesta de la AEAT", registros: "Registros", csv: "CSV" },
    envioFallo: (motivo: string) => `No llegó: ${motivo}`,
    envioSinRespuesta: "Sin respuesta legible",
    enviarAhora: "Enviar ahora",
    enviando: "Enviando…",
    enviarAhoraDetalle:
      "La cola sale sola una vez al día. Esto la empuja ya, sin saltarse el tiempo de espera que haya pedido la Agencia Tributaria.",
    envioHecho: (n: number) => `Enviado: ${n} registro(s) han salido hacia la AEAT.`,
    envioEsperando:
      "Todavía no tocaba: la Agencia Tributaria pidió esperar y ese tiempo se respeta. Vuelve a intentarlo en un minuto.",
    envioNada: "No había nada pendiente de enviar.",
    envioNoTocaba:
      "Sigue en cola: el intento anterior no llegó y el reintento va a una hora vista (art. 16.4). Míralo en «Últimos envíos».",
    envioRechazado: (n: number) => `La AEAT ha rechazado ${n} registro(s). Míralo en «Registros con defecto».`,
    envioSinConfigurar:
      "Este despliegue no puede remitir: falta el certificado o la configuración de emisión. No se ha enviado nada.",
    /*
      OD-0044 D-04. El texto dice lo que el operador tiene que entender: un
      archivo marcado puede ser un problema de ESTE cliente o del almacen, y en
      el segundo caso no estara solo. Verlo en varias fichas es la señal.
    */
    archivos: "Archivos del cliente que no cuadran",
    archivosVacio: "Ninguno. Todos los comprobados coinciden con su huella.",
    archivosDetalle:
      "Su contenido no coincide con la huella que se guardó al subirlos, o ya no están en el almacén. No se sirven ni salen en las facturas, y el cliente ve un aviso para volver a subirlos. Si aparecen en varias organizaciones a la vez, probablemente no sea cosa de un cliente: mira el registro del cron «customer-file-integrity».",
    archivosColumnas: { archivo: "Archivo", estado: "Estado", comprobado: "Comprobado" },
    archivoHashDistinto: "El contenido no es el que se subió",
    archivoNoEsta: "Ya no está en el almacén",
    comprobarArchivos: "Comprobar ahora",
    comprobando: "Comprobando…",
    /*
      Los cuatro resultados se cuentan TAL CUAL. Fingir que fue bien seria peor
      que no tener el boton: "nada que comprobar" NO es "todo correcto", y un
      `sinDeterminar` no es un archivo corrupto.
    */
    repasoVacio: "Esta empresa no tiene archivos que comprobar.",
    repasoBien: (n: number) => `${n} archivo(s) comprobado(s). Todos coinciden con su huella.`,
    repasoMal: (ok: number, mal: number) =>
      `${ok} correcto(s) y ${mal} que NO cuadran. Los marcados dejan de servirse y el cliente ve el aviso.`,
    repasoDudoso: (n: number) =>
      `${n} archivo(s) no se pudieron comprobar (el almacén no respondió). No se han marcado: se reintentan en el repaso diario.`,
    repasoSinAlmacen: "Este despliegue no tiene almacén de archivos: no hay nada que comprobar.",

    defectos: "Registros que la AEAT no dio por buenos",
    defectosVacio: "Ninguno. Todo lo enviado está aceptado.",
    defectosDetalle:
      "Subsanar manda un registro NUEVO con el mismo número de factura y el contenido de la factura congelada. El anterior no se toca: los registros no se editan jamás. Si lo que está mal es la factura, esto no es lo suyo — eso es una rectificativa.",
    defectosColumnas: { numero: "Factura", estado: "Estado", error: "Error", accion: "" },
    rechazado: "Rechazado por la AEAT",
    conErrores: "Aceptado con errores",
    duplicado: "Duplicado: en reconciliación",
    duplicadoDetalle: (estadoAeat: string | null) =>
      `La AEAT ya tenía un registro con este número${estadoAeat === null ? "" : ` (${estadoAeat})`}. No se reenvía ni se subsana: hay que comprobar en la AEAT que es este mismo registro.`,
    esSubsanacion: "(ya es una subsanación)",
    subsanar: "Subsanar",
    subsanarMotivo: "Qué defecto tenía el registro",
    subsanarMotivoAyuda: "Queda en la auditoría, y es lo que explicará dentro de un año por qué hay dos registros con el mismo número.",
    subsanarConfirmar: "Sí, subsanar",
    subsanando: "Subsanando…",
    subsanada: "Subsanación generada y encolada.",
    cerrar: "Dejar de operar",
    cerrarTitulo: "Cerrar o reabrir",
    cerrarExplicacion:
      "Cerrar una empresa hace que deje de operar: no podrá emitir ni entrar a facturar. No borra nada y se deshace. Resuelve casi todos los «dadme de baja».",
    reabrir: "Volver a operar",
    motivo: "Motivo (queda registrado)",
    motivoFalta: "Escribe el motivo: sin él no se cierra ni se reabre.",
    cerrada: "Empresa cerrada: ya no puede operar.",
    reabierta: "Empresa reabierta: vuelve a poder operar.",
    estadoCerrada: "Esta empresa está CERRADA.",
    borrar: "«Borrar» esta empresa",
    borrarExplicacion:
      "No existe, y no es un olvido. Una empresa que haya emitido facturas no se borra: sus registros son evidencia fiscal con obligación de conservación, y cinco tablas del propio esquema rechazan el DELETE. Para todo lo demás está cerrar, que se deshace (OD-0020 E-06 revisada).",
  },
  apoderamientos: {
    titulo: "Apoderamientos por comprobar",
    vacio: "No hay ninguno esperando comprobación.",
    antes: "Antes de validar: en la Sede, «Consultar apoderamientos recibidos». Comprueba que el poderdante es ese NIF, que el apoderado somos nosotros, que el poder cubre VERI*FACTU y que está vigente.",
    validar: "Validar",
    rechazar: "Rechazar",
    cancelar: "Cancelar",
    motivo: "Motivo del rechazo",
    validada: (nombre: string): string => `Autorización de ${nombre} validada.`,
    rechazada: (nombre: string): string => `Autorización de ${nombre} rechazada.`,
    columnas: { nombre: "Razón social", nif: "NIF", ruta: "Ruta", declarado: "Declarado" },
    motivos: {
      NOT_FOUND: "No aparece en la Sede",
      GRANTOR_MISMATCH: "El poderdante no es ese NIF",
      WRONG_PROCEDURE: "El poder no cubre este trámite",
      WRONG_REPRESENTATIVE: "El apoderado no somos nosotros",
      NOT_IN_FORCE: "No está vigente",
      OTHER: "Otro motivo",
    },
  },
  fundadores: {
    titulo: "Clientes fundadores",
    plazas: (libres: number, plazas: number): string => `Quedan ${libres} de ${plazas} plazas.`,
    explicacion:
      "Un año de Business gratis a cambio de dos videollamadas de opinión: antes del día 45 y antes del día 100. Si el día 100 falta alguna, sale un aviso con 15 días de margen y después se cobra la tarifa normal, un año desde ese día. Marca una llamada SOLO cuando ya se ha hecho: marcar es lo que evita el cobro, y no se puede desmarcar.",
    inscripcion: "La inscripción la hace el cupón en Stripe. Desde aquí no se puede dar de alta ni de baja a nadie.",
    vacio: "Todavía no hay ningún fundador.",
    columnas: { nombre: "Empresa", dia: "Día", llamada1: "Llamada 1", llamada2: "Llamada 2", estado: "Estado" },
    dia: (n: number): string => `día ${n}`,
    antesDe: (fecha: string): string => `antes del ${fecha}`,
    hecha: (fecha: string, por: string | null): string => (por === null ? `hecha el ${fecha}` : `hecha el ${fecha} (${por})`),
    marcar: "Marcar como hecha",
    confirmar: (n: 1 | 2, nombre: string): string =>
      `¿Confirmas que la llamada ${n} con ${nombre} YA se ha hecho? No se puede deshacer.`,
    marcada: (n: 1 | 2, nombre: string): string => `Llamada ${n} de ${nombre} marcada.`,
    sinNombre: "sin ficha en el censo",
    estados: {
      EN_CURSO: "En curso",
      CUMPLIDO: "Cumplido: año gratis",
      COBRADO: "Cobrado",
      CANCELADO: "Cancelado",
    } as Readonly<Record<string, string>>,
    avisoEnviado: (cobro: string): string => `aviso enviado: se cobra el ${cobro}`,
  },
  correos: {
    titulo: "Correos enviados",
    tituloEmpresa: "Correos",
    explicacion:
      "Los correos que manda Faktelio, con la dirección enmascarada tal como se guardó (OD-0066). Mirarlos no registra acceso a los datos de nadie. «Aceptado» quiere decir que el proveedor lo admitió, no que haya llegado: los rebotes todavía no se reciben.",
    explicacionEmpresa: "Los de esta empresa y los de sus usuarios (las recuperaciones de contraseña no llevan empresa). Las altas no salen aquí: aún no había empresa; están en Correos.",
    vacio: "Ningún correo registrado.",
    sinDato: "sin dato",
    sinDatoDetalle: "enviado antes de que se guardara el destinatario",
    columnas: {
      cuando: "Cuándo",
      correo: "Correo",
      destinatario: "Destinatario",
      empresa: "Empresa",
      estado: "Estado",
      proveedor: "Id. en el proveedor",
    },
    tipos: {
      "E-01_AEAT_RECORD_REJECTED": "Registro rechazado por la AEAT",
      "E-02_INTEGRITY_INCIDENT": "Incidencia de integridad",
      "E-03_SUBMISSION_FAILED_FINAL": "Envío a la AEAT fallido",
      "E-04_SECURITY_ACTIVITY": "Actividad de seguridad",
      "E-05_EMAIL_VERIFICATION": "Alta: verificar el correo",
      "E-06_PASSWORD_RECOVERY": "Recuperar la contraseña",
      "E-07_INVOICE_TO_CUSTOMER": "Factura al cliente",
      "E-08_CONTRACT_CONFIRMATION": "Confirmación de contrato",
      "E-09_REPRESENTATION_STEPS": "Pasos del apoderamiento",
      "E-10_FOUNDER_WELCOME": "Fundadores: bienvenida",
      "E-11_FOUNDER_CALL_REMINDER": "Fundadores: recordatorio de llamada",
      "E-12_FOUNDER_CHARGE_NOTICE": "Fundadores: aviso de cobro",
      "E-13_FOUNDER_YEAR_END": "Fundadores: fin del año",
      "E-14_TRIAL_STARTED": "Prueba de Business: empieza",
      "E-15_TRIAL_ENDING": "Prueba de Business: termina pronto",
      "E-16_TRIAL_ENDED": "Prueba de Business: terminada",
      "E-17_ADVISOR_INVITATION": "Invitación de su gestoría",
      "E-21_OPERATOR_PENDING_WORK": "Resumen diario al operador: altas y apoderamientos",
    } as Readonly<Record<string, string>>,
    roles: {
      MEMBER: "usuario de la cuenta",
      SIGNUP: "alta sin cuenta todavía",
      SELF: "el propio usuario (escrito a mano)",
      ADVISOR: "su gestoría",
      OPERATOR: "operador de Faktelio",
    } as Readonly<Record<string, string>>,
    estados: {
      SENDING: "Sin confirmar",
      SENT: "Aceptado",
      FAILED: "Falló",
    } as Readonly<Record<string, string>>,
    fallo: (motivo: string | null): string => (motivo === null ? "Falló" : `Falló: ${motivo}`),
  },
  jose: {
    titulo: "¿Ayuda José de Faktelio?",
    explicacion:
      "Contadores diarios sin ids de nadie (ADR-0024): no se ve ninguna empresa ni ningún usuario, solo cuántas veces pasó cada cosa. Las cuentas son de pasos: borrar una respuesta y volver a darla cuenta otra vez.",
    desde: "Desde",
    hasta: "Hasta",
    ver: "Ver",
    periodo: (desde: string, hasta: string): string => `Del ${desde} al ${hasta} (días UTC).`,
    periodoInvalido: "Elige un periodo de 1 a 92 días.",
    porcentaje: (n: number | null): string => (n === null ? "—" : `${n} %`),
    cualificacion: {
      titulo: "1. ¿Se cualifican las oportunidades?",
      empezadas: (n: number): string => `Empezadas: ${n}`,
      completadas: (n: number): string => `Con las cinco respuestas: ${n}`,
    },
    pasos: {
      titulo: "2. ¿Se hacen los pasos que propone?",
      total: (apuntados: number, hechos: number): string => `Apuntados: ${apuntados} · hechos: ${hechos}`,
      columnas: { paso: "Paso", apuntados: "Apuntados", hechos: "Hechos", utiles: "👍", noUtiles: "👎" },
      vacio: "Ningún paso apuntado en el periodo.",
    },
    cierres: {
      titulo: "3. ¿Ganan más las cualificadas?",
      columnas: { grupo: "Al cerrar", ganadas: "Ganadas", perdidas: "Perdidas", porcentaje: "% ganadas" },
      sinCualificar: "Sin cualificar",
      cualificadas: "Con el % de José",
      aMano: "Con el % puesto a mano",
    },
    acierto: {
      titulo: "4. ¿Acierta el porcentaje?",
      detalle: "Solo el porcentaje calculado. Si acierta, el % ganado real se parece al que decía José.",
      columnas: { porcentaje: "José decía", ganadas: "Ganadas", perdidas: "Perdidas", real: "% ganado real" },
      vacio: "Ninguna cerrada con porcentaje calculado en el periodo.",
    },
    consejos: {
      titulo: "5. ¿Qué consejos sirven?",
      detalle: "Arriba los que más 👎 tienen: son los que hay que reescribir. «Otro» es cuántas veces se pidió otro consejo en su lugar.",
      columnas: { consejo: "Consejo", utiles: "👍", noUtiles: "👎", otro: "Otro" },
      vacio: "Ninguna valoración en el periodo.",
    },
    recomendaciones: {
      titulo: "«José te recomienda»: qué puesto se abre",
      puesto: (n: string, veces: number): string => `${n}.º: ${veces}`,
    },
    /** El nombre legible de un consejo (`budget.noLoSe.03`) o de un paso (`authority.preguntar`). */
    nombre: (id: string): string => {
      const [p = "", b = "", n] = id.split(".");
      const pregunta = PREGUNTAS_JOSE[p] ?? p;
      return n === undefined ? `${pregunta} · ${b}` : `${pregunta} · ${CASOS_JOSE[b] ?? b} · nº ${n}`;
    },
  },
} as const;
